<template>
    <div v-if="this.auction">
        <div class="w-full">
            <div class="flex">
                <page-headline>{{$t('pages.auctions.planning.details.title')}}</page-headline>
                <prev-next-auction
                    class="flex pt-5 pb-3"
                    :auctions="auctions"
                    :current="auction"
                    @update="showPrevOrNext"
                />
            </div>
        </div>
        <div class="pt-4 pb-3 block xl:flex w-full ">
            <h3 class="text-md leading-6 font-medium my-auto w-full">{{ auction.name }}</h3>
            <span class="flex block whitespace-nowrap mt-3 xl:mt-0 ml-0 xl:ml-3 text-xs">
                <span class="uppercase mr-1">{{$t('shared.auction_source.last_edit')}}</span>
                {{rawAuction.updated_by?.name}} {{rawAuction.updated_by?.lastname}} |
                {{$t('shared.auction_source.sources.' + (rawAuction.source || 1))}} |
                {{ rawAuction.updated_at | formatDateAndTimeAmPm }}
            </span>
        </div>
        <div class="pt-4">
            <stats :show-headline="false">
                <stat
                    :name="$t('pages.auctions.planning.details.overview.auction_date')"
                    :value="auction.date_start | formatDay"
                ></stat>
                <stat
                    :name="$t('pages.auctions.planning.details.overview.product_id')"
                    :value="auction.article_id"
                    :value-link="linkToProdukt"
                ></stat>
                <stat
                    :name="$t('pages.auctions.planning.details.overview.total_quantity')"
                    :value="auction.article_quantity"
                ></stat>
                <stat
                    :name="$t('pages.auctions.planning.details.overview.shop_price' + this.$page.props.tenant.labels_key)"
                    :value="auction.attributes?.shop_price | toCurrency"
                ></stat>
                <stat
                    :name="$t('pages.auctions.planning.details.overview.max_price')"
                    :value="Math.max(...auction?.levels?.map(l => l.price)) | toCurrency"
                ></stat>
                <stat
                    :name="$t('pages.auctions.planning.details.overview.min_price')"
                    :value="Math.min(...auction?.levels?.map(l => l.price)) | toCurrency"
                ></stat>
                <stat
                    :name="$t('pages.auctions.planning.details.overview.levels')"
                    :value="auction?.levels?.length"
                ></stat>
            </stats>
        </div>
        <div class="mt-10">
            <div class="flex items-center justify-center">
                <h3 class="text-xl leading-6 font-medium">Levels</h3>
                <div class="sm:flex items-center sm:justify-between ml-auto">
                    <div>
                        <div class="my-auto max-w-xl text-xs text-gray-500">
                            <p>{{$t('pages.auctions.planning.details.change_data_hint')}}</p>
                        </div>
                    </div>
                    <div class="mt-5 sm:mt-0 sm:ml-6 sm:flex-shrink-0 sm:flex sm:items-center">
                        <button @click="showEditAuctionDialog()" type="button" class="btn btn-primary">
                            {{$t('pages.auctions.planning.overview.edit_button_label')}}
                        </button>
                    </div>
                    <div class="mt-5 sm:mt-0 sm:ml-6 sm:flex-shrink-0 sm:flex sm:items-center">
                        <button @click="showDeleteAuctionDialog()" type="button" class="btn btn-danger border-none">
                            {{$t('pages.auctions.planning.overview.delete_button_label')}}
                        </button>
                    </div>
                </div>
            </div>
            <alert-message
                v-if="info && info.length > 0"
                :error-type="ERROR_TYPE_INFO"
                :messages="info"
                :show-as-list="false"
                :display-errors-title="false"
                class="mb-5"
            />
            <div class="mt-4 w-full">
                <div class="w-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                    <table class="table-default">
                        <thead>
                            <tr>
                                <th scope="col">{{$t('pages.auctions.planning.details.levels_columns.level')}}</th>
                                <th scope="col">{{$t('pages.auctions.planning.details.levels_columns.price_drop')}}</th>
                                <th scope="col">{{$t('pages.auctions.planning.details.levels_columns.date_start')}}</th>
                                <th scope="col">{{$t('pages.auctions.planning.details.levels_columns.price')}}</th>
                                <th scope="col">{{$t('pages.auctions.planning.details.levels_columns.quantity')}}</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-200 bg-white">
                            <tr
                                v-if="auction.levels.length > 0"
                                v-for="level in auction.levels"
                                :key="level.id"
                                :class="{'bg-blue-50 hover:bg-blue-100': level.price_drop === 1, 'hover:bg-gray-100': level.price_drop !== 1}"
                            >
                                <td>{{ level.sequence }}</td>
                                <td><icon v-if="level.price_drop === 1" name="badge-check" class="h-6 w-6"/></td>
                                <td>{{ level.date_start | formatTime }} {{ level.date_start | formatTimeAmPm }}</td>
                                <td>{{ level.price | toCurrency }}</td>
                                <td>{{ level.quantity }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <modal
            ref="deleteAuctionDialog"
            @confirm="deleteAuction"
            @cancel="cancelAuctionDeletion"
            :dangerous="true"
            :title="this.$t('pages.auctions.planning.overview.delete_auction.title')">
            <span class="">{{this.$t('pages.auctions.planning.overview.delete_auction.text')}}</span>
        </modal>

        <modal
            ref="editAuctionDialog"
            @confirm="saveAuction"
            @cancel="cancelAuctionEditing"
            :modal-size="'l'"
            :dangerous="false"
            :title="this.$t('pages.auctions.planning.overview.edit_auction.title')"
        >
            <edit-auction-form
                ref="editAuctionForm"
                @changed="auctionFormChanged"
                :auction="rawAuction"
            />
            <alert-message
                v-if="errors && errors.length > 0"
                :error-type="ERROR_TYPE_ERROR"
                :messages="errors"
                :show-as-list="true"
                :display-errors-title="false"
                class="mb-5"
            />
        </modal>
    </div>
    <div v-else>
        {{$t('pages.auctions.planning.details.no_auctions')}}
    </div>
</template>

<script>
import AuctionState from "@/mixins/AuctionState";
import PageHeadline from "@/Shared/PageHeadline";
import Stats from "@/Shared/Stats";
import Stat from "@/Shared/Stat";
import Icon from "@/Shared/Icon";
import Modal from "@/Shared/Modals/Modal";
import PrevNextAuction from "../../Shared/PrevNextAuction";
import AdjustMaxPriceModal from "../../Shared/Modals/AdjustMaxPriceModal";
import EditAuctionForm from "../../Shared/Auction/EditAuctionForm";
import {ErrorTypes} from "../../mixins/ErrorTypes";
import AlertMessage from "@/Shared/Alerts/AlertMessage";

export default {
    mixins: [
        AuctionState,
        ErrorTypes
    ],
    components: {
        AdjustMaxPriceModal,
        PageHeadline,
        Stats,
        Stat,
        Icon,
        Modal,
        PrevNextAuction,
        EditAuctionForm,
        AlertMessage
    },
    props: {
        auctionId: {
            default: null
        },
        auction: {
            type: Object,
            default: null
        },
        rawAuction: {
            type: Object,
            default: null
        },
        auctions: [],
        productDetailsLink: {
            type: String,
            default: null
        },
        showDeleteModal: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            errors: [],
            info: null,
            savingAuction: false
        }
    },
    computed: {
        linkToProdukt() {
            return this.productDetailsLink + this.auction.article_id;
        },
    },
    mounted() {
        if (this.showDeleteModal === true) {
            this.showDeleteAuctionDialog();
        }
    },
    methods: {
        goToUpload() {
            this.$emit('go-to-upload');
        },
        showDeleteAuctionDialog() {
            this.$refs.deleteAuctionDialog?.show();
        },
        hideDeleteAuctionDialog() {
            this.$refs.deleteAuctionDialog?.hide();
        },
        showEditAuctionDialog() {
            this.info = null;
            this.$refs.editAuctionDialog?.show();
        },
        hideEditAuctionDialog() {
            this.$refs.editAuctionDialog?.hide();
        },
        deleteAuction() {
            let self = this;
            axios.delete(route('auctions.delete', {auction:this.auction.id}))
                .then(() => {
                    self.hideDeleteAuctionDialog();
                    self.$inertia.visit(route('auctions.planning'));
                })
                .catch((err) => {
                    console.error(err);
                });
        },
        cancelAuctionDeletion() {
            this.hideDeleteAuctionDialog();
            if (this.showDeleteModal === true) {
                this.$inertia.visit(route('auctions.planning'));
            }
        },
        saveAuction() {
            if (this.$refs.editAuctionForm.checkForm() && this.savingAuction === false) {
                this.savingAuction = true;
                this.errors = [];
                this.$refs.editAuctionDialog.disableConfirmButton();
                axios.post(route('auctions.planning.edit', { auction: this.auction.id, rawAuction: this.rawAuction.id }),
                    // form data as a whole
                    this.$refs.editAuctionForm.form
                )
                    .then((response) => {
                        if (response.status === 200) {
                            this.info = response.data.info;
                            this.$emit('auctionDataUpdated');
                            this.cancelAuctionEditing();
                        } else {
                            this.errors = response.data.message;
                        }
                    })
                    .catch((err) => {
                        if(err.response && err.response.data && err.response.data.errors) {
                            this.errors = err.response.data.errors;
                        } else {
                            this.errors = [err.response.data.message];
                        }
                    })
                    .finally(() => {
                        this.savingAuction = false;
                        this.$refs.editAuctionDialog.enableConfirmButton();
                    });
            }
        },
        cancelAuctionEditing() {
            this.hideEditAuctionDialog();
        },
        showPrevOrNext(auctionId){
            this.$emit('go-to-details', auctionId)
        },
        auctionFormChanged() {
            if (this.$refs.editAuctionForm.checkForm()) {
                this.$refs.editAuctionDialog.enableConfirmButton();
            } else {
                this.$refs.editAuctionDialog.disableConfirmButton();
            }
        }
    }
};
</script>
